import Reveal from "@/components/base/Reveal";
import { performanceMetrics, growthBars } from "@/mocks/home";

const toneMap: Record<string, string> = {
  primary: "text-primary-300 bg-primary-500/15",
  accent: "text-accent-300 bg-accent-500/15",
  secondary: "text-secondary-300 bg-secondary-500/15",
};

export default function Performance() {
  return (
    <section className="relative overflow-hidden bg-foreground-950 py-16 text-background-50 md:py-24">
      <div className="absolute inset-0 nx-grid opacity-30"></div>
      <div className="nx-glow absolute right-0 top-0 h-[420px] w-[420px] animate-pulseGlow"></div>

      <div className="relative mx-auto max-w-7xl px-4 md:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-background-50/15 bg-background-50/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-accent-300">
                <i className="ri-line-chart-line" aria-hidden="true"></i>
                Performance
              </span>
              <h2 className="mt-5 font-heading text-2xl font-bold tracking-tight md:text-4xl">
                Seu site precisa acompanhar o ritmo do seu negócio.
              </h2>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-background-200">
                Uma estrutura pensada para oferecer uma experiência rápida, estável e segura para os
                seus visitantes — e para o crescimento da sua empresa.
              </p>

              <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {performanceMetrics.map((metric) => (
                  <div
                    key={metric.label}
                    className="flex items-center gap-3 rounded-xl border border-background-50/10 bg-background-50/5 p-4"
                  >
                    <span
                      className={`flex h-10 w-10 items-center justify-center rounded-lg ${toneMap[metric.tone]}`}
                    >
                      <i className={`${metric.icon} text-lg`} aria-hidden="true"></i>
                    </span>
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-background-300">
                        {metric.label}
                      </p>
                      <p className="font-heading text-lg font-bold text-background-50">
                        {metric.value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="rounded-2xl border border-background-50/10 bg-foreground-900/80 p-6 backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500/20 text-primary-300">
                    <i className="ri-dashboard-3-line" aria-hidden="true"></i>
                  </span>
                  <span className="text-sm font-semibold text-background-100">
                    Painel de desempenho
                  </span>
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-500/15 px-3 py-1 text-[11px] font-medium text-secondary-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-secondary-400"></span>
                  Operacional
                </span>
              </div>

              <div className="mt-6 flex items-end gap-2 md:gap-3 h-40">
                {growthBars.map((value, index) => (
                  <div key={index} className="flex flex-1 flex-col items-center justify-end gap-2">
                    <div
                      className="w-full rounded-t-md bg-gradient-to-t from-primary-600 to-accent-400"
                      style={{ height: `${value}%` }}
                    ></div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between text-[11px] text-background-400">
                <span>Crescimento</span>
                <span className="text-secondary-300">+ tendência positiva</span>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-3">
                <MiniStat label="Uptime" value="Alta" />
                <MiniStat label="Resposta" value="Otimizada" />
                <MiniStat label="Proteção" value="Ativa" />
              </div>
            </div>
          </Reveal>
        </div>

        <p className="mt-10 text-center text-xs text-background-400">
          Indicadores meramente ilustrativos e editáveis.
        </p>
      </div>
    </section>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-background-50/10 bg-background-50/5 p-3 text-center">
      <p className="text-[11px] text-background-400">{label}</p>
      <p className="mt-1 text-sm font-semibold text-background-100">{value}</p>
    </div>
  );
}